import { AnimatePresence, motion } from 'framer-motion'
import { MouseEvent, useEffect, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import { Card } from '../../../components/card/Card'
import { DropdownMenu } from '../../../components/dropdown/DropdownMenu'
import { FilterField } from '../../../components/filterField/FilterField'
import { translations } from '../../../configs/translations'
import { Vehicle } from '../../../models/Vehicle'
import PlayerService from '../../../services/PlayerService'
import { VehicleService } from '../../../services/VehicleService'
import { useStore } from '../../../store/store'
import { arabNumberToRoman } from '../../../utils/arabNumberToRoman'
import { romanNumberToArab } from '../../../utils/romanNumberToArab'
import './Roulette.css'

interface RouletteProps {
    selectedVehicle?: Vehicle | null
    setSelectedVehicle: (vehicle: Vehicle | null) => void
}

export const Roulette = ({ selectedVehicle, setSelectedVehicle }: RouletteProps) => {
    const language = useStore(state => state.language) as keyof typeof translations
    const t = translations[language]

    const [isDragging, setIsDragging] = useState(false)
    const [startX, setStartX] = useState(0)
    const [scrollLeft, setScrollLeft] = useState(0)
    const rouletteRef = useRef<HTMLDivElement>(null)
    const [vehicles, setVehicles] = useState<Vehicle[]>([])
    const [availableBrands, setAvailableBrands] = useState<string[]>([t.all.toUpperCase()])
    const [selectedBrand, setSelectedBrand] = useState<string | undefined>(undefined)
    const [availableRanks, setAvailableRanks] = useState<number[]>([])
    const [selectedRank, setSelectedRank] = useState<number>(0)
    const [searchName, setSearchName] = useState<string>('')
    const [dragDistance, setDragDistance] = useState(0)
    const [lastDragDistance, setLastDragDistance] = useState(0)

    const fetchVehicles = async () => {
        await VehicleService.getUserVehicles({ brand: selectedBrand, rank: selectedRank, name: searchName })
            .then((response) => {
                setVehicles(response.vehicles)
                setAvailableBrands([t.all.toUpperCase(), ...response.availableBrands])
                setAvailableRanks([...response.availableRanks])
            })
            .catch((error) => {
                console.error(error)
                toast.error(
                    error && error.response && error.response.data && t[error.response.data as keyof typeof t]
                        ? t[error.response.data as keyof typeof t]
                        : t.error_unknown,
                    { className: 'notification-error' }
                )
            })
    }

    useEffect(() => {
        fetchVehicles()
    }, [selectedVehicle])

    const handleMouseDown = (e: MouseEvent) => {
        setIsDragging(true)
        if (rouletteRef.current) {
            setStartX(e.pageX - rouletteRef.current.offsetLeft)
            setScrollLeft(rouletteRef.current.scrollLeft)
        }
    }

    const handleMouseMove = (e: MouseEvent) => {
        if (!isDragging) return
        e.preventDefault()
        if (rouletteRef.current) {
            const x = e.pageX - rouletteRef.current.offsetLeft
            const walk = (x - startX) * 1.1
            rouletteRef.current.scrollLeft = scrollLeft - walk
            setDragDistance(Math.abs(x - startX))
        }
    }

    const handleMouseUp = () => {
        setLastDragDistance(dragDistance)
        setIsDragging(false)
        setDragDistance(0)
    }

    const handleMouseLeave = () => {
        setLastDragDistance(dragDistance)
        setIsDragging(false)
        setDragDistance(0)
    }

    const handleWheel = (e: React.WheelEvent) => {
        if (rouletteRef.current) {
            e.preventDefault()
            rouletteRef.current.scrollLeft += e.deltaY * 5
        }
    }

    const [selectedVehicleRequestSent, setSelectedVehicleRequestSent] = useState(false)

    const [isBrandDropdownOpen, setIsBrandDropdownOpen] = useState(false)
    const [isRankDropdownOpen, setIsRankDropdownOpen] = useState(false)

    const onVehicleSelected = async (vehicle: Vehicle) => {
        if (selectedVehicleRequestSent) return
        if (lastDragDistance > 5) return

        setSelectedVehicleRequestSent(true)
        setTimeout(() => {
            setSelectedVehicleRequestSent(false)
        }, 500)
        await VehicleService.pickVehicle(useStore.getState().userId as number, vehicle.id)
            .then((_response) => {
                setSelectedVehicle(vehicle)
            })
            .catch((error) => {
                console.error('Error happened on vehicle pick', error)
                toast.error(
                    error && error.response && error.response.data && t[error.response.data as keyof typeof t]
                        ? t[error.response.data as keyof typeof t]
                        : t.error_unknown,
                    { className: 'notification-error' }
                )
            })
    }

    useEffect(() => {

        const pickVehicleById = (vehicleId: number) => {
            if (vehicles.length === 0) return
            const vehicle = vehicles.find((vehicle) => vehicle.id === vehicleId)
            if (vehicle) {
                onVehicleSelected(vehicle)
            }
        }

        const tryToSaveOrFetchLastVehicle = async () => {
            if (selectedVehicle) {
                localStorage.setItem('lastVehicleId', String(selectedVehicle.id))
                PlayerService.trySaveLastVehicleToClient(selectedVehicle.id)
                    .catch((error) => {
                        console.warn(error)
                    })
                return
            }

            const storedLastVehicleId = parseInt(localStorage.getItem('lastVehicleId') || '-1')
            if (storedLastVehicleId) {
                pickVehicleById(storedLastVehicleId)
                PlayerService.trySaveLastVehicleToClient(storedLastVehicleId)
                    .catch((error) => {
                        console.warn(error)
                    })
            } else {
                const data = await PlayerService.tryFetchLastVehicleFromClient()
                    .catch((error) => {
                        console.warn(error)
                    })
                if (data && data.vehicleId) {
                    const vehicleId = data.vehicleId
                    pickVehicleById(vehicleId)
                    localStorage.setItem('lastVehicleId', String(vehicleId))
                }
            }
        }

        tryToSaveOrFetchLastVehicle()
    }, [selectedVehicle, vehicles])

    const isFirstFilterRender = useRef(true)

    useEffect(() => {
        if (isFirstFilterRender.current) {
            isFirstFilterRender.current = false
            return
        }
        fetchVehicles()
    }, [selectedBrand, selectedRank])

    const isFirstSearchRender = useRef(true)

    useEffect(() => {
        if (isFirstSearchRender.current) {
            isFirstSearchRender.current = false
            return
        }
        const timeout = setTimeout(() => {
            fetchVehicles()
        }, 400)
        return () => clearTimeout(timeout)
    }, [searchName])

    const setOpenRankDropdown = (open: boolean) => {
        if (open) setIsBrandDropdownOpen(false)
        setIsRankDropdownOpen(open)
    }

    const setOpenBrandDropdown = (open: boolean) => {
        if (open) setIsRankDropdownOpen(false)
        setIsBrandDropdownOpen(open)
    }

    const inputVehicleSearch = (newValue: string) => {
        const MAX_SYMBOLS_FOR_VEHICLE_NAME = 30
        if (newValue.length > MAX_SYMBOLS_FOR_VEHICLE_NAME) {
            setSearchName(newValue.slice(0, MAX_SYMBOLS_FOR_VEHICLE_NAME))
            return
        }
        setSearchName(newValue)
    }

    const getRankChildren = () => {
        const romanRanksSorted = [...availableRanks.sort((a, b) => b - a).map(arabNumberToRoman)]
        return [...romanRanksSorted, t.all]
            .map((rank) =>
                <div
                    className='dropdown-item'
                    key={rank}
                    onClick={() => {
                        setSelectedRank(romanNumberToArab(rank))
                        setIsRankDropdownOpen(false)
                    }}
                >
                    {rank}
                </div>
            )
    }

    return (
        <div className="roulette-main-container">
            <div className="filters">
                <DropdownMenu
                    title={selectedBrand || t.all}
                    minWidth={150}
                    children={
                        availableBrands.map((brand) =>
                            <div
                                className='dropdown-item'
                                key={brand}
                                onClick={() => {
                                    if (brand === t.all.toUpperCase()) {
                                        setSelectedBrand(undefined)
                                    } else {
                                        setSelectedBrand(brand)
                                    }
                                    setIsBrandDropdownOpen(false)
                                }}
                            >
                                {brand}
                            </div>
                        )
                    }
                    isOpen={isBrandDropdownOpen}
                    setIsOpen={setOpenBrandDropdown}
                />
                <DropdownMenu
                    title={selectedRank ? arabNumberToRoman(selectedRank) : t.all}
                    minWidth={100}
                    children={getRankChildren()}
                    isOpen={isRankDropdownOpen}
                    setIsOpen={setOpenRankDropdown}
                />
                <FilterField
                    value={searchName}
                    label={t.search_vehicle_by_name}
                    icon={'../icons/search.svg'}
                    onChange={(newValue) => {
                        inputVehicleSearch(newValue)
                    }}
                />
            </div>
            <div
                className="roulette"
                ref={rouletteRef}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseLeave}
                onWheel={handleWheel}
            >
                <AnimatePresence mode="popLayout">
                    {vehicles.map((vehicle) => (
                        <motion.div
                            key={vehicle.id}
                            layout
                            initial={{ opacity: 0, scale: 0.8, y: 16 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.8, y: -16 }}
                            transition={{ type: 'spring', stiffness: 400, damping: 30 }}
                        >
                            <Card
                                isSelected={selectedVehicle?.id === vehicle.id}
                                name={vehicle.name}
                                brand={vehicle.brand}
                                driveBias={vehicle.type as 'FWD' | 'RWD' | 'AWD'}
                                carRank={vehicle.rank}
                                onSelect={async () => {
                                    if (isDragging) return
                                    await onVehicleSelected(vehicle)
                                }}
                            />
                        </motion.div>
                    ))}
                </AnimatePresence>
            </div>
        </div>
    )
}
